import { SVGProps } from 'react';

interface IconProps extends SVGProps<SVGSVGElement> {
  size?: number;
  weight?: 'thin' | 'light' | 'regular' | 'bold' | 'fill' | 'duotone';
}

const strokeWidths = {
  thin: 1,
  light: 1.5,
  regular: 2,
  bold: 2.5,
  fill: 0,
  duotone: 2,
};

// ═══════════════════════════════════════════════════════════════
// DOG FACE - Front-facing friendly dog
// ═══════════════════════════════════════════════════════════════
export function DogFace({ size = 24, weight = 'regular', ...props }: IconProps) {
  const strokeWidth = strokeWidths[weight];
  const isFill = weight === 'fill';
  const isDuotone = weight === 'duotone';

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      {/* Head - round */}
      {isDuotone && (
        <circle cx="12" cy="13" r="9" fill="currentColor" opacity={0.2} />
      )}
      <circle
        cx="12"
        cy="13"
        r="9"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        fill={isFill ? 'currentColor' : 'none'}
      />

      {/* Left ear - floppy, hangs down */}
      {isDuotone && (
        <ellipse cx="5" cy="10" rx="3" ry="5" fill="currentColor" opacity={0.3} />
      )}
      <ellipse
        cx="5"
        cy="10"
        rx="3"
        ry="5"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        fill={isFill ? 'currentColor' : 'none'}
      />

      {/* Right ear - floppy, hangs down */}
      {isDuotone && (
        <ellipse cx="19" cy="10" rx="3" ry="5" fill="currentColor" opacity={0.3} />
      )}
      <ellipse
        cx="19"
        cy="10"
        rx="3"
        ry="5"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        fill={isFill ? 'currentColor' : 'none'}
      />

      {/* Face details - only when not filled */}
      {!isFill && (
        <>
          {/* Eyes */}
          <circle cx="9" cy="11" r="1.5" fill="currentColor" />
          <circle cx="15" cy="11" r="1.5" fill="currentColor" />

          {/* Snout area - lighter oval */}
          <ellipse
            cx="12"
            cy="16"
            rx="4"
            ry="3"
            stroke="currentColor"
            strokeWidth={strokeWidth * 0.75}
            fill="none"
          />

          {/* Nose */}
          <ellipse cx="12" cy="14" rx="2" ry="1.5" fill="currentColor" />

          {/* Mouth */}
          <path
            d="M10 17 Q12 19 14 17"
            stroke="currentColor"
            strokeWidth={strokeWidth * 0.75}
            strokeLinecap="round"
            fill="none"
          />
        </>
      )}
    </svg>
  );
}

// ═══════════════════════════════════════════════════════════════
// PAW PRINT - Classic dog paw icon with 4 toes and main pad
// ═══════════════════════════════════════════════════════════════
export function PawPrint({ size = 24, weight = 'regular', ...props }: IconProps) {
  const strokeWidth = strokeWidths[weight];
  const isFill = weight === 'fill';
  const isDuotone = weight === 'duotone';

  const shouldFill = isFill || isDuotone;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      {/* Main pad - large oval/bean shape at bottom */}
      <ellipse
        cx="12"
        cy="16"
        rx="5"
        ry="4"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        fill={shouldFill ? 'currentColor' : 'none'}
        opacity={isDuotone ? 0.3 : 1}
      />

      {/* Four toe beans in an arc above the main pad */}
      {/* Outer left toe */}
      <ellipse
        cx="5"
        cy="9"
        rx="2"
        ry="2.5"
        transform="rotate(-20 5 9)"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        fill={shouldFill ? 'currentColor' : 'none'}
      />

      {/* Inner left toe */}
      <ellipse
        cx="9"
        cy="6.5"
        rx="2"
        ry="2.5"
        transform="rotate(-5 9 6.5)"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        fill={shouldFill ? 'currentColor' : 'none'}
      />

      {/* Inner right toe */}
      <ellipse
        cx="15"
        cy="6.5"
        rx="2"
        ry="2.5"
        transform="rotate(5 15 6.5)"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        fill={shouldFill ? 'currentColor' : 'none'}
      />

      {/* Outer right toe */}
      <ellipse
        cx="19"
        cy="9"
        rx="2"
        ry="2.5"
        transform="rotate(20 19 9)"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        fill={shouldFill ? 'currentColor' : 'none'}
      />
    </svg>
  );
}

// ═══════════════════════════════════════════════════════════════
// BONE - Dog treat/reward icon
// ═══════════════════════════════════════════════════════════════
export function Bone({ size = 24, weight = 'regular', ...props }: IconProps) {
  const strokeWidth = strokeWidths[weight];
  const isFill = weight === 'fill';
  const isDuotone = weight === 'duotone';

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      {isDuotone && (
        <path
          d="M6 9H18V15H6V9Z"
          fill="currentColor"
          opacity={0.2}
        />
      )}
      <path
        d="M17 6C18.6569 6 20 7.34315 20 9C20 10.3062 19.1652 11.4175 18 11.8293V12.1707C19.1652 12.5825 20 13.6938 20 15C20 16.6569 18.6569 18 17 18C15.6938 18 14.5825 17.1652 14.1707 16H9.82929C9.41746 17.1652 8.30622 18 7 18C5.34315 18 4 16.6569 4 15C4 13.6938 4.83481 12.5825 6 12.1707V11.8293C4.83481 11.4175 4 10.3062 4 9C4 7.34315 5.34315 6 7 6C8.30622 6 9.41746 6.83481 9.82929 8H14.1707C14.5825 6.83481 15.6938 6 17 6Z"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill={isFill ? 'currentColor' : 'none'}
      />
    </svg>
  );
}

// ═══════════════════════════════════════════════════════════════
// LEASH - Training/walking icon
// ═══════════════════════════════════════════════════════════════
export function Leash({ size = 24, weight = 'regular', ...props }: IconProps) {
  const strokeWidth = strokeWidths[weight];
  const isFill = weight === 'fill';
  const isDuotone = weight === 'duotone';

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      {isDuotone && (
        <circle cx="6" cy="6" r="3" fill="currentColor" opacity={0.2} />
      )}
      {/* Handle loop */}
      <circle
        cx="6"
        cy="6"
        r="3"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        fill={isFill ? 'currentColor' : 'none'}
      />
      {/* Leash line */}
      <path
        d="M9 6C9 6 12 8 12 12C12 16 15 18 18 18"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Clip */}
      <path
        d="M18 15V21M16 18H20"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ═══════════════════════════════════════════════════════════════
// COLLAR - Simple dog collar with hanging tag
// ═══════════════════════════════════════════════════════════════
export function Collar({ size = 24, weight = 'regular', ...props }: IconProps) {
  const strokeWidth = strokeWidths[weight];
  const isFill = weight === 'fill';
  const isDuotone = weight === 'duotone';

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      {isDuotone && (
        <>
          <path
            d="M2 8C2 8 4 4 12 4C20 4 22 8 22 8C22 8 20 12 12 12C4 12 2 8 2 8Z"
            fill="currentColor"
            opacity={0.2}
          />
          <circle cx="12" cy="18" r="4" fill="currentColor" opacity={0.3} />
        </>
      )}

      {/* Collar band - open U shape around neck */}
      <path
        d="M2 8C2 8 4 4 12 4C20 4 22 8 22 8"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />

      {/* Collar band bottom curve */}
      <path
        d="M4 10C4 10 6 12 12 12C18 12 20 10 20 10"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />

      {/* Buckle - small rectangle */}
      <rect
        x="10"
        y="6"
        width="4"
        height="3"
        rx="0.5"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        fill={isFill ? 'currentColor' : 'none'}
      />

      {/* Ring connector */}
      <circle
        cx="12"
        cy="12"
        r="1"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        fill={isFill ? 'currentColor' : 'none'}
      />

      {/* Tag - bone-shaped */}
      <path
        d="M9 16C8 16 7 16.5 7 17.5C7 18.5 8 19 9 19H15C16 19 17 18.5 17 17.5C17 16.5 16 16 15 16H9Z"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill={isFill ? 'currentColor' : 'none'}
      />

      {/* Tag connector line */}
      <path
        d="M12 13V16"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
    </svg>
  );
}

// ═══════════════════════════════════════════════════════════════
// TRAINING - Whistle/Training icon
// ═══════════════════════════════════════════════════════════════
export function Training({ size = 24, weight = 'regular', ...props }: IconProps) {
  const strokeWidth = strokeWidths[weight];
  const isFill = weight === 'fill';
  const isDuotone = weight === 'duotone';

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      {isDuotone && (
        <path
          d="M5 11L7 9L10 12L8 14L5 11Z"
          fill="currentColor"
          opacity={0.2}
        />
      )}
      {/* Target/Goal */}
      <circle
        cx="16"
        cy="8"
        r="4"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        fill={isFill ? 'currentColor' : 'none'}
      />
      <circle
        cx="16"
        cy="8"
        r="1.5"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        fill={isFill ? 'none' : 'none'}
      />
      {/* Path to goal */}
      <path
        d="M4 20L8 16L12 12"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray={isDuotone ? '4 2' : 'none'}
      />
      {/* Checkpoints */}
      <circle cx="8" cy="16" r="1" fill="currentColor" />
    </svg>
  );
}

// ═══════════════════════════════════════════════════════════════
// PROGRESS PAW - Paw with progress indicator
// ═══════════════════════════════════════════════════════════════
export function ProgressPaw({ size = 24, weight = 'regular', ...props }: IconProps) {
  const strokeWidth = strokeWidths[weight];
  const isFill = weight === 'fill';
  const isDuotone = weight === 'duotone';

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      {/* Progress circle background */}
      <circle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        opacity={0.2}
      />
      {/* Progress arc (75% example) */}
      <path
        d="M12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        fill="none"
      />
      {/* Small paw in center */}
      <g transform="translate(8, 8) scale(0.33)">
        <ellipse
          cx="12"
          cy="17"
          rx="4"
          ry="3"
          fill={isDuotone || isFill ? 'currentColor' : 'none'}
          stroke="currentColor"
          strokeWidth={strokeWidth * 3}
        />
        <ellipse cx="7" cy="10" rx="2" ry="2.5" fill="currentColor" />
        <ellipse cx="17" cy="10" rx="2" ry="2.5" fill="currentColor" />
        <ellipse cx="9.5" cy="6" rx="1.5" ry="2" fill="currentColor" />
        <ellipse cx="14.5" cy="6" rx="1.5" ry="2" fill="currentColor" />
      </g>
    </svg>
  );
}

// ═══════════════════════════════════════════════════════════════
// BALL - Play/Game icon
// ═══════════════════════════════════════════════════════════════
export function Ball({ size = 24, weight = 'regular', ...props }: IconProps) {
  const strokeWidth = strokeWidths[weight];
  const isFill = weight === 'fill';
  const isDuotone = weight === 'duotone';

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      {isDuotone && (
        <circle cx="12" cy="12" r="9" fill="currentColor" opacity={0.2} />
      )}
      {/* Main ball */}
      <circle
        cx="12"
        cy="12"
        r="9"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        fill={isFill ? 'currentColor' : 'none'}
      />
      {/* Tennis ball style curved lines */}
      {!isFill && (
        <>
          <path
            d="M5 8C8 10 8 14 5 16"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            fill="none"
          />
          <path
            d="M19 8C16 10 16 14 19 16"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            fill="none"
          />
        </>
      )}
    </svg>
  );
}

// ═══════════════════════════════════════════════════════════════
// TRICK STAR - Star with sparkle for tricks
// ═══════════════════════════════════════════════════════════════
export function TrickStar({ size = 24, weight = 'regular', ...props }: IconProps) {
  const strokeWidth = strokeWidths[weight];
  const isFill = weight === 'fill';
  const isDuotone = weight === 'duotone';

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      {isDuotone && (
        <path
          d="M12 2L14.5 9H22L16 13.5L18.5 21L12 16.5L5.5 21L8 13.5L2 9H9.5L12 2Z"
          fill="currentColor"
          opacity={0.2}
        />
      )}
      {/* Main star */}
      <path
        d="M12 2L14.5 9H22L16 13.5L18.5 21L12 16.5L5.5 21L8 13.5L2 9H9.5L12 2Z"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill={isFill ? 'currentColor' : 'none'}
      />
      {/* Sparkle dots */}
      {!isFill && (
        <>
          <circle cx="20" cy="4" r="1" fill="currentColor" />
          <circle cx="4" cy="4" r="0.75" fill="currentColor" />
        </>
      )}
    </svg>
  );
}

// ═══════════════════════════════════════════════════════════════
// TROPHY PAW - Trophy for weekly games/achievements
// ═══════════════════════════════════════════════════════════════
export function TrophyPaw({ size = 24, weight = 'regular', ...props }: IconProps) {
  const strokeWidth = strokeWidths[weight];
  const isFill = weight === 'fill';
  const isDuotone = weight === 'duotone';

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      {isDuotone && (
        <path
          d="M6 4H18V10C18 13.3137 15.3137 16 12 16C8.68629 16 6 13.3137 6 10V4Z"
          fill="currentColor"
          opacity={0.2}
        />
      )}
      {/* Trophy cup */}
      <path
        d="M6 4H18V10C18 13.3137 15.3137 16 12 16C8.68629 16 6 13.3137 6 10V4Z"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        fill={isFill ? 'currentColor' : 'none'}
      />
      {/* Left handle */}
      <path
        d="M6 6H4C3 6 2 7 2 8C2 9 3 10 4 10H6"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        fill="none"
      />
      {/* Right handle */}
      <path
        d="M18 6H20C21 6 22 7 22 8C22 9 21 10 20 10H18"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        fill="none"
      />
      {/* Base */}
      <path
        d="M12 16V18M8 21H16M10 18H14V21H10V18Z"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      {/* Small paw inside trophy */}
      {!isFill && (
        <g transform="translate(9.5, 7) scale(0.25)">
          <ellipse cx="12" cy="16" rx="5" ry="4" fill="currentColor" />
          <ellipse cx="5" cy="9" rx="2" ry="2.5" fill="currentColor" />
          <ellipse cx="19" cy="9" rx="2" ry="2.5" fill="currentColor" />
          <ellipse cx="9" cy="5" rx="2" ry="2.5" fill="currentColor" />
          <ellipse cx="15" cy="5" rx="2" ry="2.5" fill="currentColor" />
        </g>
      )}
    </svg>
  );
}

// ═══════════════════════════════════════════════════════════════
// DOG SILHOUETTE - Sitting dog profile silhouette (facing right)
// Based on classic Labrador sitting pose
// ═══════════════════════════════════════════════════════════════
export function DogSilhouette({ size = 24, weight = 'regular', ...props }: IconProps) {
  const isDuotone = weight === 'duotone';

  // Clean sitting dog silhouette path
  const sitPath = "M20.5,7.2c0,0-0.8-1.5-0.3-2.8c0.5-1.3,1.8-1.8,1.8-1.8s-0.5-1.3,0.5-1.8c1-0.5,2,0.3,2,0.3s1-1,2.3-0.5c1.3,0.5,1.3,1.8,1.3,1.8s1.5,0.8,1.5,2.5s-1,2.5-1,2.5l0.5,1c0,0,1.5,1,1.5,3s-1.3,3-2.5,3.3c0,0,0,2.5-0.5,4l0,3.5c0,0.5-0.5,1-1,1h-1.5c-0.5,0-1-0.5-1-1l0-2.5l-3,0l0,2.5c0,0.5-0.5,1-1,1h-1.5c-0.5,0-1-0.5-1-1l0-4c0,0-2-1.5-2-5c0,0-1.5-0.5-1.5-2.5s1.3-2.5,1.8-2.5C16.2,8,17.5,7.5,20.5,7.2z";

  return (
    <svg
      width={size}
      height={size}
      viewBox="12 0 20 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      {isDuotone && (
        <path d={sitPath} fill="currentColor" opacity={0.3} />
      )}
      <path d={sitPath} fill="currentColor" />
    </svg>
  );
}

// ═══════════════════════════════════════════════════════════════
// TREAT - Dog treat/bonus content icon
// ═══════════════════════════════════════════════════════════════
export function Treat({ size = 24, weight = 'regular', ...props }: IconProps) {
  const strokeWidth = strokeWidths[weight];
  const isFill = weight === 'fill';
  const isDuotone = weight === 'duotone';

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      {isDuotone && (
        <path
          d="M12 3C14.5 3 16 5 16 7C16 9 14 10 14 12C14 14 16 15 16 17C16 19 14.5 21 12 21C9.5 21 8 19 8 17C8 15 10 14 10 12C10 10 8 9 8 7C8 5 9.5 3 12 3Z"
          fill="currentColor"
          opacity={0.2}
        />
      )}
      {/* Treat shape - like a dog biscuit */}
      <path
        d="M12 3C14.5 3 16 5 16 7C16 9 14 10 14 12C14 14 16 15 16 17C16 19 14.5 21 12 21C9.5 21 8 19 8 17C8 15 10 14 10 12C10 10 8 9 8 7C8 5 9.5 3 12 3Z"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill={isFill ? 'currentColor' : 'none'}
      />
      {/* Decorative dots */}
      {!isFill && (
        <>
          <circle cx="12" cy="7" r="1" fill="currentColor" />
          <circle cx="12" cy="12" r="1" fill="currentColor" />
          <circle cx="12" cy="17" r="1" fill="currentColor" />
        </>
      )}
    </svg>
  );
}

// Export all icons
export const PetIcons = {
  DogFace,
  DogSilhouette,
  PawPrint,
  Bone,
  Leash,
  Collar,
  Training,
  ProgressPaw,
  Ball,
  TrickStar,
  TrophyPaw,
  Treat,
};

export default PetIcons;
